import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { StoryConfig } from '@frontend/sports/common/client-config-data-access';
import { DialogAnimation, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { DeviceService, TimerService } from '@frontend/vanilla/core';
import { skip } from 'rxjs';

import { RedirectHelperService } from '../../navigation-core/redirect-helper.service';
import { StoryContentTrackingService } from '../story-content-tracking.service';
import { ContentStory, ContentSubStory, StoryNavigation, SubStoryType } from '../story-content.model';
import { StoryContentService } from '../story-content.service';
import { StoryDetailsComponent } from './story-details.component';
import { SubStoryBodyComponent } from './sub-story-body.component';

@Component({
    selector: 'ms-story-body',
    templateUrl: './story-body.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SubStoryBodyComponent, NgClass],
})
export class StoryBodyComponent {
    @Input() stories: ContentStory[];
    @Input() activeStory: number;
    @Input() activeSubStory: number;
    @Input() paused: boolean;
    @Output() close = new EventEmitter<void>();

    get story(): ContentStory {
        return this.stories[this.activeStory];
    }

    get subStory(): ContentSubStory {
        return this.story.subStories[this.activeSubStory];
    }

    controlsVisible = false;
    private restrictStatusUpdate = false;
    private dialog: ModalRef | undefined;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private redirectHelperService: RedirectHelperService,
        private modalDialogService: ModalDialogService,
        private storyConfig: StoryConfig,
        private storyContentTrackingService: StoryContentTrackingService,
        private timerService: TimerService,
        private storyContentService: StoryContentService,
        private deviceService: DeviceService,
    ) {
        this.deviceService.orientation.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
            this.dialog?.close();
            this.restrictStatusUpdate = true;
            this.dialog = undefined;
        });
    }

    handlePanEvent(hammerEvent: { direction: number; deltaY: number }, subStory: ContentSubStory): void {
        if (Math.abs(hammerEvent.deltaY) > this.storyConfig.minSwipeDistance) {
            if (hammerEvent.direction === Hammer.DIRECTION_DOWN) {
                this.close.emit();

                return;
            }
            if (hammerEvent.direction === Hammer.DIRECTION_UP && subStory.ctaEnabled && subStory.type !== SubStoryType.Promotion) {
                this.handleCTA(subStory.ctaRedirectionLink);

                return;
            }
        }
        this.setProgressBarStatus(false);
    }

    handleCTA(ctaRedirectionLink: string, event?: Event, ctaButtonClicked: boolean = false): void {
        event?.preventDefault();

        if (ctaButtonClicked) {
            this.storyContentTrackingService.trackCtaButtonClick(this.story.title);
        }
        if (ctaRedirectionLink) {
            this.close.emit();
            this.redirectHelperService.goToPage(ctaRedirectionLink);

            return;
        }
        if (this.dialog) {
            return;
        }

        this.setProgressBarStatus(true);
        this.restrictStatusUpdate = false;
        this.dialog = this.modalDialogService.openPopup(StoryDetailsComponent, {
            data: {
                title: this.story.title,
                subStory: this.subStory,
            },
            settings: {
                cssClass: 'modal-popup story-content-modal',
                fit: false,
                showPageHeader: false,
                showDialogHeader: false,
                isModalDialog: true,
                animation: DialogAnimation.SlideInFromBottom,
                closeAnimation: DialogAnimation.SlideOutFromTop,
                backdropClass: 'story-details-popup-no-backdrop',
                afterResulted: () => {
                    this.dialog = undefined;
                    if (!this.restrictStatusUpdate) {
                        this.setProgressBarStatus(false);
                    }
                    this.changeDetectorRef.markForCheck();
                },
            },
        });
    }

    nextStory(): void {
        this.storyContentService.navigate(this.getNavigationPayload(StoryNavigation.NextStory));
    }

    previousStory(): void {
        this.storyContentService.navigate(this.getNavigationPayload(StoryNavigation.PreviousStory));
    }

    nextSubStory(): void {
        this.storyContentService.navigate(this.getNavigationPayload(StoryNavigation.NextSubStory));
    }

    previousSubStory(): void {
        this.storyContentService.navigate(this.getNavigationPayload(StoryNavigation.PreviousSubStory));
    }

    private getNavigationPayload(action: StoryNavigation) {
        return {
            action: action,
            payload: { storyId: this.story.id, subStoryId: this.subStory.id },
        };
    }

    setProgressBarStatus(pauseProgressBar: boolean): void {
        if (pauseProgressBar) {
            this.storyContentService.pause();
        } else {
            this.storyContentService.play();
        }
    }

    controlsClick(): void {
        this.controlsVisible = true;
        this.setProgressBarStatus(!this.paused);

        this.timerService.setTimeout(() => {
            this.controlsVisible = false;
        });
    }
}
